import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Clock, Phone, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that appears when a customer hits /order outside opening hours.
 * - Polls /api/status to honour the same source-of-truth as the order gate.
 * - Closes itself automatically the moment the restaurant opens.
 */
export default function ClosedModal({ onStatus }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/status`);
        if (!alive) return;
        setStatus(data);
        setOpen(!data.is_open);
        onStatus?.(data);
      } catch {
        // If we can't reach the server, default to "open" so we don't block
        // legitimate ordering during preview/dev.
        if (alive) {
          setStatus({ is_open: true });
          setOpen(false);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [onStatus]);

  if (loading || !status || status.is_open) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md text-center" data-testid="closed-modal">
        <div className="mx-auto rounded-full bg-[#C9853E]/10 text-[#C9853E] p-3 w-fit">
          <Clock className="w-7 h-7" />
        </div>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl md:text-3xl font-extrabold text-[#0E0E1A]">
            Sorry — we're closed right now.
          </DialogTitle>
          <DialogDescription className="text-[#57534E] leading-relaxed text-base">
            Online ordering is paused while the kitchen is off.{" "}
            <strong className="text-[#0E0E1A]">
              We open at {status.next_open_label}.
            </strong>{" "}
            Come back then — we'll have the tandoor fired up.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-2.5">
          <a
            href="tel:+14039422565"
            data-testid="closed-call-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-6 py-3.5 font-bold text-sm transition-all"
          >
            <Phone className="w-4 h-4" /> Call us · (403) 942-2565
          </a>
          <Link
            to="/menu"
            data-testid="closed-browse-btn"
            className="inline-flex items-center justify-center gap-2 text-[#C9853E] hover:text-[#A6692D] font-bold text-sm py-2"
          >
            <ArrowLeft className="w-4 h-4" /> Browse the menu while you wait
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
